import { FC } from 'react';

import _ from 'lodash';
import { useFormContext } from 'react-hook-form';

import { YES_NO_OPTIONS } from 'constants/common';
import { TeethExtraction } from 'types/TreatmentPlanning.types';
import { translate } from 'utils';

import { FormLabel } from '../FormLabel/FormLabel';
import RhfRadioGroup from '../RadioGroup/RhfRadioGroup/RhfRadioGroup';
import RhfToothChart from '../ToothChart/RhfToothChart';

const DEFAULT_FIELD_PREFIX =
  'treatmentSpecifications.ZenClearUltra.auxiliaries';
const TOOTH_CHART_POSITIONS: (keyof TeethExtraction)[] = [
  'rightTop',
  'rightBottom',
  'leftTop',
  'leftBottom'
];

interface Props {
  isDisabled?: boolean;
  isUpperDisabled?: boolean;
  isLowerDisabled?: boolean;
  isToothChartRestricted?: boolean;
  fieldPrefix?: string;
  className?: string;
  label?: string;
}

const Auxiliaries: FC<Props> = ({
  isDisabled,
  isUpperDisabled,
  isLowerDisabled,
  isToothChartRestricted,
  fieldPrefix = DEFAULT_FIELD_PREFIX,
  className,
  label = translate('prescription.step17_auxiliaries')
}) => {
  const {
    watch,
    formState: { errors }
  } = useFormContext();
  const watchIsPresent = watch(`${fieldPrefix}.isPresent`);
  const toothInformationChart = isToothChartRestricted
    ? watch(
        fieldPrefix.replace(/\.auxiliaries$/, '.toothInformation.toothChart')
      )
    : undefined;
  const extractionToothChart = isToothChartRestricted
    ? watch(fieldPrefix.replace(/\.auxiliaries$/, '.extraction.toothChart'))
    : undefined;
  const restrictedToothChartValue = isToothChartRestricted
    ? TOOTH_CHART_POSITIONS.reduce(
        (mergedChart, position) => ({
          ...mergedChart,
          [position]: _.uniqBy(
            [
              ...(toothInformationChart?.[position] || []),
              ...(extractionToothChart?.[position] || [])
            ],
            'value'
          )
        }),
        {} as TeethExtraction
      )
    : undefined;
  const fieldError = _.get(errors, fieldPrefix);

  return (
    <div className='flex flex-col'>
      <FormLabel
        label={label}
        className={`md:text-17px mb-4 ${className}`}
        error={!!fieldError}
        name='auxiliaries'
      />
      <RhfRadioGroup
        inputProps={{ disabled: isDisabled }}
        options={YES_NO_OPTIONS}
        name={`${fieldPrefix}.isPresent`}
        className='flex flex-row space-x-16'
        radioClassName='border-RADIO_BORDER'
        radioItemFont={`md:text-17px text-sm ${className}`}
      />
      {watchIsPresent === true && (
        <div className='bg-GRAY5 p-4 rounded-2xl mt-4 flex flex-col space-y-6'>
          <div>
            <p className='mb-3 text-14px'>
              {translate('prescription.auxiliary_buttons')}
            </p>
            <RhfToothChart
              isBaby={false}
              showError={false}
              name={`${fieldPrefix}.buttons`}
              disabled={isDisabled}
              isUpperDisabled={isUpperDisabled}
              isLowerDisabled={isLowerDisabled}
              isToothChartRestricted={isToothChartRestricted}
              restrictedToothChartValue={restrictedToothChartValue}
            />
          </div>
          <div>
            <p className='mb-3 text-14px'>
              {translate('prescription.auxiliary_precision_cuts')}
            </p>
            <RhfToothChart
              isBaby={false}
              showError={false}
              name={`${fieldPrefix}.precisionCuts`}
              disabled={isDisabled}
              isUpperDisabled={isUpperDisabled}
              isLowerDisabled={isLowerDisabled}
              isToothChartRestricted={isToothChartRestricted}
              restrictedToothChartValue={restrictedToothChartValue}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Auxiliaries;
